import type { NoteType, PaginatedResponse } from "types/DataTypes";

import { api } from "./api";

type GetNotesArgs = {
  file?: number;
  service?: number;
  lien?: number;
  lien_file?: number;
  claim?: number;
  mentionee?: number;
  created_by?: number;
  page?: number;
  page_size?: number;
};

type CreateNoteArgs = {
  data: {
    text: string;
    file?: number;
    service?: number;
    lien?: number;
    lien_file?: number;
    claim?: number;
    mentionee?: number;
  };
};

type UpdateNoteArgs = {
  id: number;
  data: {
    text: string;
    file?: number;
    service?: number;
    lien?: number;
    lien_file?: number;
    claim?: number;
    mentionee?: number | null;
  };
};

export const notesAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    getNotes: builder.query<PaginatedResponse<NoteType>, GetNotesArgs>({
      query: ({
        file,
        service,
        lien,
        lien_file,
        claim,
        mentionee,
        created_by,
        page = 1,
        page_size = 20,
      }) => {
        const params = new URLSearchParams();
        params.set("page", page.toString());
        params.set("page_size", page_size.toString());

        if (file) params.set("file", file.toString());
        if (service) params.set("service", service.toString());
        if (lien) params.set("lien", lien.toString());
        if (lien_file) params.set("lien_file", lien_file.toString());
        if (claim) params.set("claim", claim.toString());
        if (mentionee) params.set("mentionee", mentionee.toString());
        if (created_by) params.set("created_by", created_by.toString());

        return {
          url: `api/v1/notes/?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({
                type: "Note" as const,
                id,
              })),
              { type: "Notes", id: "LIST" },
            ]
          : [{ type: "Notes", id: "LIST" }],
    }),

    getNoteById: builder.query<NoteType, { id: number }>({
      query: ({ id }) => ({
        url: `api/v1/notes/${id}/`,
        method: "GET",
      }),
      providesTags: (result, error, { id }) => [{ type: "Note", id }],
    }),

    createNote: builder.mutation<NoteType, CreateNoteArgs>({
      query: ({ data }) => ({
        url: "api/v1/notes/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Notes", id: "LIST" }],
    }),

    updateNote: builder.mutation<NoteType, UpdateNoteArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/notes/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Note", id },
        { type: "Notes", id: "LIST" },
      ],
    }),

    deleteNote: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `api/v1/notes/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Note", id },
        { type: "Notes", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetNotesQuery,
  useGetNoteByIdQuery,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
} = notesAPI;
